/** Dashboard + report data hooks — spec §5-F13, §7.7. */

import { useQuery } from '@tanstack/react-query';
import { api, qs } from '@/lib/api';

export interface PersonalDashboard {
  openByPriority: Record<string, number>;
  overdue: number;
  completedLast7Days: number;
  hoursLoggedThisWeek: number;
  capacityHoursThisWeek: number;
  reviewQueue: number;
}

export interface ProjectDashboard {
  statusDistribution: { statusId: string; name: string; category: string; count: number }[];
  burnUp: { date: string; scope: number; completed: number }[];
  cycleTimeDays: { median: number; p85: number; sample: number };
  overdue: { key: string; title: string; daysOverdue: number }[];
  blocked: { key: string; title: string; reason?: string; days: number }[];
  topContributors: { userId: string; name: string; completed: number }[];
  estimateAccuracy: { estimated: number; spent: number; ratio: number };
  openVsClosed: { week: string; opened: number; closed: number }[];
  timeCompliance?: { completed: number; withoutTime: number; percentage: number };
}

export interface ExecutiveDashboard {
  throughput: { week: string; projectKey: string; completed: number }[];
  cycleTime: { medianDays: number; p85Days: number; sample: number };
  leadTime: { medianDays: number; p85Days: number; sample: number };
  blocked: { count: number; longest?: { key: string; days: number; reason?: string } };
  overdueByProject: { projectKey: string; count: number }[];
  overdueByPerson: { userId: string; name: string; count: number }[];
  capacity: { userId: string; name: string; utilisation: number }[];
  bugRatio: { projectKey: string; percentage: number }[];
  reopenRate: number;
  wipByPerson: { userId: string; name: string; wip: number; flagged: boolean }[];
  onLeaveToday: string[];
}

export function usePersonalDashboard() {
  return useQuery({
    queryKey: ['dashboard', 'personal'],
    queryFn: async () => (await api.get<PersonalDashboard>('/reports/dashboard/personal')).data,
  });
}

export function useProjectDashboard(projectId?: string) {
  return useQuery({
    queryKey: ['dashboard', 'project', projectId],
    enabled: Boolean(projectId),
    queryFn: async () =>
      (await api.get<ProjectDashboard>(`/reports/dashboard/project/${projectId}`)).data,
  });
}

export function useExecutiveDashboard(enabled = true) {
  return useQuery({
    queryKey: ['dashboard', 'executive'],
    enabled,
    queryFn: async () => (await api.get<ExecutiveDashboard>('/reports/dashboard/executive')).data,
  });
}

/**
 * The three named reports share one row shape: `{ key, label, <metric> }`.
 *
 * `groupBy` decides what a row *is* — a project, a person or a week — which is
 * why the label is generic rather than `projectKey`. The executive dashboard's
 * `throughput` field is a different, week-by-project shape; do not confuse them.
 */
export interface ThroughputRow {
  key: string;
  label: string;
  completed: number;
}

export function useThroughput(filters: { projectId?: string; groupBy?: 'project' | 'user' | 'week' } = {}) {
  return useQuery({
    queryKey: ['report', 'throughput', filters],
    queryFn: async () => (await api.get<ThroughputRow[]>(`/reports/throughput${qs(filters)}`)).data,
  });
}

export interface CycleTimeRow {
  week: string;
  medianDays: number;
  sample: number;
}

export function useCycleTime(filters: { projectId?: string } = {}) {
  return useQuery({
    queryKey: ['report', 'cycle-time', filters],
    queryFn: async () => (await api.get<CycleTimeRow[]>(`/reports/cycle-time${qs(filters)}`)).data,
  });
}

export interface TimeReportRow {
  key: string;
  label: string;
  minutes: number;
}

export function useTimeReport(filters: { groupBy?: 'user' | 'project' | 'task' } = {}) {
  return useQuery({
    queryKey: ['report', 'time', filters],
    queryFn: async () => (await api.get<TimeReportRow[]>(`/reports/time${qs(filters)}`)).data,
  });
}

/** CSV export URL for any report (§F13). */
export function reportExportUrl(name: string, params: Record<string, unknown> = {}): string {
  return `/api/v1/reports/${name}/export${qs(params)}`;
}
